'use client';

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import type {
  CompanyBasicResponse,
  CompanyRegisterResponse,
  CompanyContactResponse,
  CompanyGesellschafterResponse,
  CompanyFinancialsResponse,
  CompanyNetworkResponse,
} from '@/lib/backend-api';
import {
  previewBasicSchema,
  previewRegisterSchema,
  previewContactSchema,
  previewGesellschafterSchema,
  previewFinancialsSchema,
  previewNetworkSchema,
  previewEnvelopeSchema,
} from '@/lib/company-preview-schemas';

const previewKeys = ['company-preview'] as const;
// Session cache includes the viewer so privileged responses cannot survive a
// sign-out or permission change and become visible to another viewer.
const cache = new Map<string, unknown>();

class PreviewRequestError extends Error {
  constructor(readonly status: number) {
    super(`Company preview request failed (${status})`);
  }
}

function usePreviewSection<T>(
  euId: string,
  viewer: string,
  section: string,
  schema: z.ZodType,
  enabled: boolean,
) {
  return useQuery<T | null>({
    queryKey: [...previewKeys, viewer, euId, section],
    queryFn: async ({ signal }) => {
      const cacheKey = JSON.stringify([viewer, euId, section]);
      if (cache.has(cacheKey)) return cache.get(cacheKey) as T | null;
      const path =
        section === 'network' ? 'network?max_nodes=30' : `unified/${section}`;
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) abort();
      try {
        const response = await fetch(
          `/api/companies/${encodeURIComponent(euId)}/${path}`,
          { signal: controller.signal },
        );
        if (!response.ok) {
          if (section !== 'basic' && [401, 403, 404, 429].includes(response.status))
            return null;
          throw new PreviewRequestError(response.status);
        }
        const raw: unknown = await response.json();
        const envelope = previewEnvelopeSchema.parse(raw);
        const parsed = schema.parse(envelope.data) as T;
        if (!signal.aborted) cache.set(cacheKey, parsed);
        return parsed;
      } finally {
        signal.removeEventListener('abort', abort);
      }
    },
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
}

/** Independent queries render progressively and consume React Query's abort
 * signal. The explicit close cleanup also aborts while the exit animation runs. */
export function useCompanyPreviewData(
  euId: string,
  viewer: string,
  active: boolean,
) {
  const client = useQueryClient();
  useEffect(() => {
    if (!active)
      void client.cancelQueries({ queryKey: [...previewKeys, viewer, euId] });
    return () => {
      void client.cancelQueries({ queryKey: [...previewKeys, viewer, euId] });
    };
  }, [active, client, euId, viewer]);
  const basic = usePreviewSection<CompanyBasicResponse>(
    euId,
    viewer,
    'basic',
    previewBasicSchema,
    active,
  );
  // Check basic first, including its private-company guard, before requesting
  // the rest of the preview's sections.
  const enabled = active && !!basic.data;
  const register = usePreviewSection<CompanyRegisterResponse>(
    euId,
    viewer,
    'register',
    previewRegisterSchema,
    enabled,
  );
  const contact = usePreviewSection<CompanyContactResponse>(
    euId,
    viewer,
    'contact',
    previewContactSchema,
    enabled,
  );
  const gesellschafter = usePreviewSection<CompanyGesellschafterResponse>(
    euId,
    viewer,
    'gesellschafter',
    previewGesellschafterSchema,
    enabled,
  );
  const financials = usePreviewSection<CompanyFinancialsResponse>(
    euId,
    viewer,
    'financials?refresh=false',
    previewFinancialsSchema,
    enabled && basic.data?.register_type !== 'VR',
  );
  const network = usePreviewSection<CompanyNetworkResponse>(
    euId,
    viewer,
    'network',
    previewNetworkSchema,
    enabled && !basic.data?.is_deleted,
  );
  return {
    basicData: basic.data ?? null,
    registerData: register.data ?? null,
    contactData: contact.data ?? null,
    gesellschafterData: gesellschafter.data ?? null,
    financialsData: financials.data ?? null,
    networkData:
      network.data && network.data.nodes.length > 1 ? network.data : null,
    isLoading: basic.isPending && !basic.error,
    isRegisterLoading: register.isFetching,
    isGesellschafterLoading: gesellschafter.isFetching,
    loadingBackground: register.isFetching || financials.isFetching,
    notFound:
      basic.error instanceof PreviewRequestError && basic.error.status === 404,
    error: basic.error,
  };
}
